import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { RoomType, UseType } from "@/commons/services/Rooms";
import { HitomeqErrorType } from "@/commons/models/SystemError";
import { Waiter } from "./Waiters";

/**
 * 利用者 API 操作クラス
 */
export default class Residents {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * GET Residents
   */
  public async get(userStore: LoginUser, residentId: number): Promise<ResidentGetModel> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      residentId: residentId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Resident, formatParams);
    const response = await this.commonService.executeGet<ResidentGetModel>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * GET Residents
   */
  public async gets(
    userStore: LoginUser,
    queryParams?: { startIndex?: number; endIndex?: number; existFamily?: boolean; masking?: string; addDeleted?: "true" | "false" }
  ): Promise<Array<ResidentGetModel>> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Residents, formatParams);
    const params: any = queryParams ? queryParams : {};
    const response = await this.commonService.executeGet<Array<ResidentGetModel>>(userStore, requestUrl, this.constructor.name, params, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * GET ResidentsCount
   */
  public async getCount(userStore: LoginUser, queryParams?: { existFamily?: boolean; addDeleted?: "true" | "false" }): Promise<number> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Residents, formatParams);
    const params: any = queryParams ? queryParams : {};
    // countの場合は末尾に「count」をつける
    const response = await this.commonService.executeGet<{ count: number }>(userStore, `${requestUrl}/count`, this.constructor.name, params, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data.count;
  }

  /**
   * POST Residents
   */
  public async post(userStore: LoginUser, requestBody: ResidentPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Residents, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * PATCH Residents
   */
  public async patch(userStore: LoginUser, residentId: number, requestBody: ResidentPatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      residentId: residentId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Resident, formatParams);
    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * PATCH Resident Status
   */
  public async patchStatus(userStore: LoginUser, residentId: number, requestBody: ResidentStatusPatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      residentId: residentId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.ResidentStatus, formatParams);
    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * DELETE Resident
   */
  public async delete(userStore: LoginUser, residentId: number, requestBody: ResidentDeleteModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      residentId: residentId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Resident, formatParams);
    const response = await this.commonService.executeDelete<any>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0, requestBody);

    return response.data;
  }

  /**
   * POST Residents Transfer
   */
  public async postTransfer(userStore: LoginUser, residentId: number, requestBody: ResidentTransferPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      residentId: residentId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.ResidentTransfer, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  public async getsForHitomeQCareSupport(userStore: LoginUser): Promise<ResidentGetForHitomeQCareSupportModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.ResidentsForHitomeQCareSupport, formatParams);
    const response = await this.commonService.executeGet<ResidentGetForHitomeQCareSupportModel>(
      userStore,
      requestUrl,
      this.constructor.name,
      {},
      HitomeqErrorType.REQUEST_RETRY,
      0
    );
    return response.data;
  }

  public async getsForIMedic(userStore: LoginUser): Promise<ResidentGetForIMedicModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.ResidentsForIMedic, formatParams);
    const response = await this.commonService.executeGet<ResidentGetForIMedicModel>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);
    return response.data;
  }
}

export interface ResidentGetModel {
  /** 施設を示す ID */
  facilityId: number;
  /** ソートキー (residentId)_(familyId) */
  residentsSortKey: string;
  /** 利用者ID */
  id: number;
  /** 家族ID(0固定) */
  familyId: number;
  /** システムで一意のID */
  idInSystem: number;

  /** 状態(待機者画面ではステップと呼称している) */
  status: ResidentStatus;

  /** 利用者名（ふりがな） */
  kanaName?: string;
  /** 利用者名 */
  name: string;
  /** 性別 */
  gender: string;
  /** 利用者または家族の誕生日(YYYYMMDDhhmmssSSS) */
  birthday: any;
  /** パスワード */
  password: string;
  /** 家族数 */
  familyCount: number;
  /** 電話番号 */
  tel: string;
  /** 部屋ID */
  roomId: number;
  /** 部屋名 */
  room: string;
  /** 部屋用途 */
  roomUseType: UseType;
  /** 部屋種別 */
  roomType: RoomType;
  /** 有効期限(YYYYMMDDhhmmssSSS) */
  validPeriodTo: number;
  /** グループID */
  groupId: number;
  /** グループ名 */
  groupName: string;

  /** 入退所情報 */
  inAndOutDatetimes: Array<InAndOutDatetime>;
  /** 保険者番号 */
  insuredNumber: string | null;
  /** 介護保険被保険者番号 */
  careInsuranceInsuredNumber: string | null;

  /** 待機者情報、待機者から利用者に変更された場合は値が存在する */
  waiter: Waiter;

  /** 論理削除フラグ */
  deleteFlag: boolean;
  /** 更新日時(YYYYMMDDhhmmssSSS) */
  updated: number;
  /** 登録日時(YYYYMMDDhhmmssSSS) */
  created: number;
}

export enum ResidentStatus {
  /** 不明 */
  UNKNOWN = 0,
  /** 登録者(未使用) */
  REGISTERD = 1,
  /** 取り下げ */
  CANCELED = 5,
  /** 一時保存 */
  TEMPORARILY = 8,
  /** 入居申込み */
  APPLICATION = 10,
  /** 申込み受理 */
  RECEIVED_APPLICATION = 20,
  /** 判定済 */
  COMPLETE_APPLICATION = 30,
  /** 入居意向確認 */
  CONFIRM_ADMISSION = 40,
  /** 最終判定中 */
  FINAL_JUDGE = 50,
  /** 最終判定済み */
  COMPLETE_FINAL_JUDGE = 60,
  /** 入居手続き */
  ADMISSION_PROCEDURE = 70,
  /** 利用者 */
  RESIDENT = 100
}

export interface InAndOutDatetime {
  /** 入所予定日時 */
  inDatetime: number | null;
  /** 退所予定日時 */
  outDatetime: number | null;
  /** 入所日の送迎時間 */
  pickUpDatetime: number | null;
  /** 退所日の送迎時間 */
  dropOffDatetime: number | null;
  /** 部屋ID */
  roomId: number;
}

export interface ResidentPostModel {
  /** 利用者誕生日(YYYYMMDDhhmmssSSS) */
  birthday: number | null;
  /** 性別 */
  gender: string;
  /** 利用者名（ふりがな） */
  kanaName?: string;
  /** 利用者名 */
  name: string;
  /** 電話番号 */
  tel: string;
  /** 部屋ID */
  roomId: number | null;
  /** グループID */
  groupId: number;
  /** 入退所情報 */
  inAndOutDatetimes: Array<InAndOutDatetime>;
  /** 保険者番号 */
  insuredNumber: string | null;
  /** 介護保険被保険者番号 */
  careInsuranceInsuredNumber: string | null;
}

export interface ResidentPatchModel {
  /** 利用者誕生日(YYYYMMDDhhmmssSSS) */
  birthday: number | null;
  /** 性別 */
  gender: string;
  /** 利用者名（ふりがな） */
  kanaName?: string;
  /** 利用者名 */
  name: string;
  /** 電話番号 */
  tel: string;
  /** 部屋ID */
  roomId: number | null;
  /** グループID */
  groupId: number;
  /** 入退所情報 */
  inAndOutDatetimes: Array<InAndOutDatetime>;
  /** 保険者番号 */
  insuredNumber: string | null;
  /** 介護保険被保険者番号 */
  careInsuranceInsuredNumber: string | null;
  /** 更新日時(YYYYMMDDhhmmssSSS) */
  updated: number;
  /** 論理削除フラグ */
  deleteFlag: boolean;
}

export interface ResidentStatusPatchModel {
  /** 変更後の利用者状態 */
  changedStatus: ResidentStatus;
  /** 更新日時(YYYYMMDDhhmmssSSS) */
  updated: number;
}

export interface ResidentDeleteModel {
  /** 削除メッセージタイトル */
  deleteMessageTitle: string;
  /** 削除メッセージ本文 */
  deleteMessageText: string;
}

export interface ResidentTransferPostModel {
  /** 移動先施設ID */
  destinationFacilityId: number;

  /** 部屋ID */
  roomId: number;
  /** グループID */
  groupId: number;
  /** 入退所情報 */
  inAndOutDatetimes: Array<InAndOutDatetime>;

  /** 移動後、現施設から削除するか否か */
  needDelete: boolean;
}

export interface ResidentGetForHitomeQCareSupportModel {
  /** 新規登録対象利用者 */
  residentsForRegistration: Array<ResidentWithDiff>;
  /** 変更対象利用者 */
  residentsForUpdate: Array<ResidentWithDiff>;
  /** 削除対象利用者 */
  residentsForDelete: Array<ResidentWithRoomAndGroup>;
}

/**
 * ### 新規登録・削除対象利用者
 * - 画面表示で必要な部屋名、利用者グループ名を追加している
 */
export interface ResidentWithRoomAndGroup {
  /** 施設を示す ID */
  facilityId: number;
  /** ソートキー (residentId)_(familyId) */
  residentsSortKey?: string;

  /** システムで一意のID */
  idInSystem?: number;
  /** 利用者名を示す ID */
  id?: number;
  /** 利用者名（ふりがな） */
  kanaName?: string;
  /** 利用者名の名前 */
  name: string;
  /** 性別 */
  gender: string;
  /** 利用者名または家族の誕生日(YYYYMMDDhhmmssSSS) */
  birthday?: number;
  /** 電話番号 */
  tel?: string;
  /** 部屋ID */
  roomId?: number;
  /** 部屋名 */
  room?: string;
  /** 利用者グループを示す ID */
  groupId: number;
  /** 利用者グループ名 */
  residentGroupName?: string;
  /** 家族数 */
  familyCount?: number;

  /** 保険者番号 */
  insuredNumber?: string;
  /** 介護保険被保険者番号 */
  careInsuranceInsuredNumber?: string;

  /** 入退所情報 */
  inAndOutDatetimes: Array<InAndOutDatetime>;

  /** 最終更新日 */
  updated?: number;
  /** 作成した日付(YYYYMMDDhhmmssSSS) */
  created?: number;
}

/**
 * ### 変更対象利用者
 * - ケアサポートの情報は差分として返却するの利用者情報に追加している
 */
export interface ResidentWithDiff extends ResidentWithRoomAndGroup {
  /** 利用者名（ふりがな） */
  csResidentKanaName: string | null;
  /** 利用者名 */
  csResidentName: string;
  /** 性別 */
  csGender: string;
  /** 生年月日 */
  csBirthday: number;
  /** 電話番号 */
  csTel: string | null;
  /** 部屋ID */
  csRoomId: number | null;
  /** 部屋 */
  csRoomName: string | null;

  /** 保険者番号 */
  csInsuredNumber: string;
  /** 介護保険被保険者番 */
  csCareInsuranceInsuredNumber: string;

  /** 入所日 */
  csInDatetime: number | null;
  /** 退所日 */
  csOutDatetime: number | null;
}

export interface ResidentGetForIMedicModel {
  /** 新規登録対象利用者 */
  residentsForRegistration: Array<ResidentWithDiff>;
  /** 変更対象利用者 */
  residentsForUpdate: Array<ResidentWithDiff>;
  /** 削除対象利用者 */
  residentsForDelete: Array<ResidentWithRoomAndGroup>;
}
